import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import BlockContent from '@sanity/block-content-to-react';
import cx from 'classnames';

import { getJobPostings } from 'state/actions/jobPostingsActions';

import { LinkOrButton, Button } from 'components/base';
import { ModuleHeader } from 'components/modules';
import { BasePortableText, AccordionArrow } from 'styled';

import Language from 'constants/Language';
import {
  ExternalLink,
  InternalLink,
  ListItem,
} from 'constants/PortableTextSerializer';

import {
  GeneralModuleColorScheme,
  GeneralModuleColor,
} from 'constants/ColorScheme';

import {
  JobPostingsModule as IJobPostingsModule,
  JobPostings,
  GlobalState,
} from 'types';

interface PassedProps {
  module: IJobPostingsModule;
  hasWrapper?: boolean;
  hasCallout?: boolean;
}

interface StoreProps {
  jobPostings: JobPostings | null;
}

interface DispatchProps {
  actions: {
    getJobPostings(): void;
  };
}

type Props = PassedProps & StoreProps & DispatchProps;

interface State {
  selectedItem: string | null;
}

class JobPostingsModule extends Component<Props, State> {
  state: State = {
    selectedItem: null,
  };

  componentDidMount() {
    if (!this.props.jobPostings) {
      this.props.actions.getJobPostings();
    }
  }

  handleItemClick = (id: string) => {
    this.setState((state) => ({
      selectedItem: id === state.selectedItem ? null : id,
    }));
  };

  render() {
    const { module, hasCallout, hasWrapper, jobPostings } = this.props;
    const { selectedItem } = this.state;
    const { moduleHeader, button, bottomDescription } = module;
    const headerTitle = moduleHeader.title;
    const headerDescription = moduleHeader.description;
    const colorScheme =
      GeneralModuleColorScheme[
        module.moduleOptions.color as GeneralModuleColor
      ];
    const textIsBlack = colorScheme.text === 'black';
    const renderModuleHeader = headerTitle || headerDescription;

    return (
      <div
        className={cx(
          'JobPostingsModule w100 module-content-padding-x flex-1 pb6',
          {
            [`bg-color-${colorScheme.background} color-${colorScheme.text}`]: !hasWrapper,
            pt6: !renderModuleHeader,
            'module-content-padding-with-callout': hasCallout,
          }
        )}
      >
        {renderModuleHeader && (
          <div className="JobPostingsModule__header">
            <ModuleHeader title={headerTitle} description={headerDescription} />
          </div>
        )}
        <div className="JobPostingsModule__items flex flex-col">
          {jobPostings &&
            Object.keys(jobPostings).map((department) => {
              return (
                <div key={department} className="JobPostingsModule__department">
                  <div className="JobPostingsModule__department-title mt2_5 mb1">
                    <span className="text-lg color-gray-dark">
                      {department}
                    </span>
                  </div>
                  {jobPostings[department].map((item, index) => {
                    const isFirstItem = index === 0;
                    const itemIsExpanded = selectedItem === item.id;

                    return (
                      <div key={item.id} className="overflow-y-hidden">
                        <div
                          className={cx(
                            `AccordionListModule__item relative flex flex-col text-left py1_5 overflow-y-hidden color-${colorScheme.text}`,
                            {
                              'border-top-black-opacity-20':
                                isFirstItem && textIsBlack,
                              'border-top-gray': isFirstItem && !textIsBlack,
                              'border-bottom-black-opacity-20': textIsBlack,
                              'border-bottom-gray': !textIsBlack,
                            }
                          )}
                        >
                          <Button
                            key={item.id}
                            ariaLabel={Language.t(
                              `Global.${
                                itemIsExpanded
                                  ? 'collapseButtonAriaLabel'
                                  : 'expandButtonAriaLabel'
                              }`,
                              {
                                section: item.title,
                              }
                            )}
                            containerClassName="w100"
                            onClick={() => this.handleItemClick(item.id)}
                            className="link-border-bottom-appear-on-hover"
                          >
                            <div className="JobPostingsModule__item-trigger">
                              <div className="flex flex-row justify-between items-start md:items-center">
                                <div className="flex flex-col items-start text-left">
                                  <p
                                    className={cx('text-md', {
                                      'color-white': !textIsBlack,
                                    })}
                                  >
                                    <span
                                      className={cx({
                                        'link-border-bottom-black-hidden': textIsBlack,
                                        'link-border-bottom-white-hidden': !textIsBlack,
                                      })}
                                    >
                                      {item.title}
                                    </span>
                                  </p>
                                  {!!item.location && (
                                    <p
                                      className={cx(
                                        'text-sm mt1 none md:inline-flex',
                                        {
                                          'color-gray-dark': textIsBlack,
                                          'color-gray-lighter': !textIsBlack,
                                        }
                                      )}
                                    >
                                      {item.location}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center">
                                  <LinkOrButton
                                    ariaLabel={Language.t(
                                      'JobPostingModule.applyNowButtonAriaLabel',
                                      { title: item.title }
                                    )}
                                    variant="primary-md-white"
                                    label={Language.t(
                                      'JobPostingModule.applyNowButtonLabel'
                                    )}
                                    to={item.link}
                                    containerClassName="none md:block mr2"
                                  />
                                  <AccordionArrow
                                    className="JobPostingsModule__item-arrow ml1_5"
                                    isReverse={itemIsExpanded}
                                    color={colorScheme.text}
                                  />
                                </div>
                              </div>
                              {!!item.location && (
                                <div className="flex md:none justify-start items-start">
                                  <p
                                    className={cx('text-sm mt1', {
                                      'color-gray-dark': textIsBlack,
                                      'color-gray-lighter': !textIsBlack,
                                    })}
                                  >
                                    {item.location}
                                  </p>
                                </div>
                              )}
                            </div>
                          </Button>
                          <div
                            className={cx(
                              'JobPostingsModule__item-expandable transition-short text-secondary-sm col-12 md:col-10',
                              {
                                'transition-open-short mt1_5': itemIsExpanded,
                                'transition-close-short m0': !itemIsExpanded,
                              }
                            )}
                            aria-hidden={!itemIsExpanded}
                          >
                            <div
                              className={cx(
                                'JobPostingsModule__item-text text-secondary-sm transition-short',
                                {
                                  'JobPostingsModule__item-text-expanded opacity-1': itemIsExpanded,
                                  'opacity-0': !itemIsExpanded,
                                }
                              )}
                            >
                              <div className="flex justify-start md:none">
                                <LinkOrButton
                                  ariaLabel={Language.t(
                                    'JobPostingModule.applyNowButtonAriaLabel',
                                    { title: item.title }
                                  )}
                                  variant="primary-md-white"
                                  label={Language.t(
                                    'JobPostingModule.applyNowButtonLabel'
                                  )}
                                  to={item.link}
                                />
                              </div>
                              <BasePortableText
                                className="JobPostingsModule__description mt2"
                                dangerouslySetInnerHTML={{
                                  __html: item.description,
                                }}
                              />
                              <div className="flex justify-start mt2">
                                <LinkOrButton
                                  ariaLabel={Language.t(
                                    'JobPostingModule.applyNowButtonAriaLabel',
                                    { title: item.title }
                                  )}
                                  variant="primary-md"
                                  label={Language.t(
                                    'JobPostingModule.applyNowButtonLabel'
                                  )}
                                  to={item.link}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
        {!!bottomDescription.length && (
          <BasePortableText className="JobPostingsModule__bottom-description md:col-10 text-secondary-sm mt1_75">
            <BlockContent
              blocks={bottomDescription}
              serializers={{
                listItem: ListItem,
                marks: { link: ExternalLink, internalLink: InternalLink },
              }}
            />
          </BasePortableText>
        )}
        {!!button.label && !!button.url && (
          <LinkOrButton
            ariaLabel={button.label}
            containerClassName="inline-block mt1_75"
            label={button.label}
            to={button.url}
            variant={
              `underline-${colorScheme.text}` as
                | 'underline-black'
                | 'underline-white'
            }
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: GlobalState): StoreProps => ({
  jobPostings: state.jobPostings,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  actions: bindActionCreators(
    {
      getJobPostings,
    },
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(JobPostingsModule);
